// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getUniverseData,
  getAllLocations,
  getAllPositions,
  getUniverseInfo,
  isWarpGate,
  getWarpGate
} from '../config/universes';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

const UniverseContext = createContext();

export const useUniverse = () => {
  const context = useContext(UniverseContext);
  if (!context) {
    throw new Error('useUniverse must be used within UniverseProvider');
  }
  return context;
};

// Helper function to load initial state from localStorage
const loadInitialState = () => {
  const saved = localStorage.getItem(STORAGE_KEYS.UNIVERSE_STATE);
  
  if (saved) {
    try {
      const state = JSON.parse(saved);
      
      return {
        currentUniverse: state.currentUniverse || 'milkyway',
        discoveredUniverses: state.discoveredUniverses || ['milkyway'],
        universeHistory: state.universeHistory || ['milkyway']
      };
    } catch (error) {
      console.error('[UniverseContext] Failed to load universe state:', error);
    }
  }
  
  // Default state if no saved data
  return {
    currentUniverse: 'milkyway',
    discoveredUniverses: ['milkyway'],
    universeHistory: ['milkyway']
  };
};

export const UniverseProvider = ({ children }) => {
  // Initialize state from localStorage immediately (not in useEffect)
  const initialState = loadInitialState();
  
  // Current universe
  const [currentUniverse, setCurrentUniverse] = useState(initialState.currentUniverse);
  
  // Discovered universes (milkyway is always discovered)
  const [discoveredUniverses, setDiscoveredUniverses] = useState(initialState.discoveredUniverses);
  
  // Universe travel history (for breadcrumb trail)
  const [universeHistory, setUniverseHistory] = useState(initialState.universeHistory);

  // Save to localStorage whenever state changes
  useEffect(() => {
    const state = {
      currentUniverse,
      discoveredUniverses,
      universeHistory,
      // Note: discoveredGates removed - now handled by NavigationPanel's discoveredSystems
    };
    localStorage.setItem(STORAGE_KEYS.UNIVERSE_STATE, JSON.stringify(state));
  }, [currentUniverse, discoveredUniverses, universeHistory]);

  // Get current universe data
  const getCurrentUniverseData = () => {
    return getUniverseData(currentUniverse);
  };

  // Get all locations in current universe
  const getCurrentLocations = () => {
    return getAllLocations(currentUniverse);
  };

  // Get all positions in current universe
  const getCurrentPositions = () => {
    return getAllPositions(currentUniverse);
  };

  // Get current universe info
  const getCurrentUniverseInfo = () => {
    return getUniverseInfo(currentUniverse);
  };

  // Travel through warp gate
  const travelThroughGate = (gateId) => {
    const gate = getWarpGate(gateId, currentUniverse);
    
    if (!gate) {
      console.error('Gate not found:', gateId);
      return false;
    }

    const destinationUniverse = gate.destinationUniverse;
    
    // Add destination to discovered universes
    if (!discoveredUniverses.includes(destinationUniverse)) {
      setDiscoveredUniverses(prev => [...prev, destinationUniverse]);
    }

    // Add to history
    setUniverseHistory(prev => [...prev, destinationUniverse]);

    // Switch universe
    setCurrentUniverse(destinationUniverse);

    // Note: Gate discovery now handled by NavigationPanel's discoveredSystems

    return true;
  };

  // Reset universe state (for game reset)
  const resetUniverseState = () => {
    setCurrentUniverse('milkyway');
    setDiscoveredUniverses(['milkyway']);
    setUniverseHistory(['milkyway']);
    localStorage.removeItem(STORAGE_KEYS.UNIVERSE_STATE);
  };

  const value = {
    // State
    currentUniverse,
    discoveredUniverses,
    universeHistory,
    
    // Getters
    getCurrentUniverseData,
    getCurrentLocations,
    getCurrentPositions,
    getCurrentUniverseInfo,
    
    // Gate functions
    travelThroughGate,
    
    // Utility
    isWarpGate,
    resetUniverseState,
  };

  return (
    <UniverseContext.Provider value={value}>
      {children}
    </UniverseContext.Provider>
  );
};
